import React, { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";

function makeId(str) {
  return String(str)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export default function Accordion({ items }) {
  const reduceMotion = useReducedMotion();
  const [openId, setOpenId] = useState(items?.[0]?.id || null);

  const normalized = useMemo(
    () =>
      (items || []).map((it, idx) => ({
        id: it.id || `${makeId(it.q || "item")}-${idx}`,
        q: it.q,
        a: it.a,
      })),
    [items]
  );

  return (
    <div className="mx-auto mt-10 max-w-3xl divide-y divide-fk-border overflow-hidden rounded-2xl border border-fk-border bg-fk-surface/70 shadow-[0_1px_0_rgba(0,0,0,0.02),0_22px_70px_rgba(11,18,32,0.10)] dark:divide-white/10 dark:border-white/10 dark:bg-gray-900/60 dark:shadow-[0_1px_0_rgba(255,255,255,0.04),0_22px_70px_rgba(0,0,0,0.45)]">
      {normalized.map((it) => {
        const isOpen = openId === it.id;
        const buttonId = `acc-btn-${it.id}`;
        const panelId = `acc-panel-${it.id}`;
        return (
          <div key={it.id} className="p-0">
            <button
              id={buttonId}
              type="button"
              className="flex w-full items-center justify-between gap-4 px-5 py-5 text-left text-sm font-semibold text-fk-ink hover:bg-fk-ink/[0.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fk-accent focus-visible:ring-offset-2 focus-visible:ring-offset-fk-surface dark:text-gray-100 dark:hover:bg-white/5 dark:focus-visible:ring-offset-gray-950"
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => setOpenId(isOpen ? null : it.id)}
            >
              <span className="text-pretty">{it.q}</span>
              <motion.span
                aria-hidden="true"
                initial={false}
                animate={{ rotate: isOpen ? 180 : 0 }}
                transition={{ duration: reduceMotion ? 0 : 0.2 }}
                className="shrink-0"
              >
                <ChevronDown className="h-5 w-5 text-fk-muted dark:text-gray-300" />
              </motion.span>
            </button>

            <AnimatePresence initial={false}>
              {isOpen ? (
                <motion.div
                  id={panelId}
                  role="region"
                  aria-labelledby={buttonId}
                  initial={reduceMotion ? { opacity: 1, height: "auto" } : { opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={reduceMotion ? { opacity: 1, height: "auto" } : { opacity: 0, height: 0 }}
                  transition={{ duration: reduceMotion ? 0 : 0.25, ease: "easeOut" }}
                  className="overflow-hidden"
                >
                  <div className="px-5 pb-5 text-sm leading-relaxed text-fk-muted dark:text-gray-300">
                    {it.a}
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}

